import React from 'react';
import { useTrussVisibilityControls } from '../../hooks/useTrussVisibilityControls';

interface TrussVisibilityControlsProps {
  className?: string;
}

export const EnhancedTrussVisibilityToggle: React.FC<TrussVisibilityControlsProps> = ({ 
  className = "" 
}) => {
  const {
    areAllNodeTrussesVisible,
    areAllBeamTrussesVisible,
    getNodeTrussCount,
    getBeamTrussCount,
    toggleAllNodeTrusses,
    toggleAllBeamTrusses
  } = useTrussVisibilityControls();

  // Get current state for both truss types
  const nodeTrussCount = getNodeTrussCount();
  const beamTrussCount = getBeamTrussCount();

  // Render button for specific truss type
  const renderTrussButton = (type: 'beam' | 'node') => {
    const isBeam = type === 'beam';
    const trussCount = isBeam ? beamTrussCount : nodeTrussCount;
    const isVisible = isBeam ? areAllBeamTrussesVisible : areAllNodeTrussesVisible;
    const trussTypeLabel = isBeam ? 'Beam' : 'Node';
    
    // Don't show button if no trusses of this type exist
    if (trussCount === 0) {
      return null;
    }

    const buttonText = isVisible ? `Hide ${trussTypeLabel} Truss` : `Show ${trussTypeLabel} Truss`;
    const buttonColor = isVisible 
      ? 'bg-red-500 hover:bg-red-600 text-white' 
      : 'bg-green-500 hover:bg-green-600 text-white';

    const handleClick = () => {
      if (isBeam) {
        toggleAllBeamTrusses();
      } else {
        toggleAllNodeTrusses();
      }
    };

    return (
      <button
        key={type}
        className={`px-3 py-1 text-sm rounded-md transition-colors ${buttonColor}`}
        onClick={handleClick}
        title={`${buttonText} (${trussCount} total)`}
      >
        <span className="flex items-center">
          <span className="mr-1">
            {isBeam ? '━' : '●'}
          </span>
          {buttonText}
        </span>
      </button>
    );
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      {renderTrussButton('node')}
      {renderTrussButton('beam')}
    </div>
  );
};

// Utility functions using the hook
export const createTrussVisibilityUtils = () => {
  const controls = useTrussVisibilityControls();
  
  return {
    showTruss: controls.showTruss,
    hideTruss: controls.hideTruss,
    toggleTrussButtons: controls.toggleTrussButtons,
    
    // Additional helper functions
    showAllNodeTrusses: controls.showAllNodeTrusses,
    hideAllNodeTrusses: controls.hideAllNodeTrusses,
    showAllBeamTrusses: controls.showAllBeamTrusses,
    hideAllBeamTrusses: controls.hideAllBeamTrusses,
    
    // State getters
    getNodeTrussCount: controls.getNodeTrussCount,
    getBeamTrussCount: controls.getBeamTrussCount,
    areAllNodeTrussesVisible: controls.areAllNodeTrussesVisible,
    areAllBeamTrussesVisible: controls.areAllBeamTrussesVisible
  };
};
